import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Activity, CheckCircle, AlertTriangle, XCircle } from "lucide-react";

const scoreColor = (score) => {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-500";
  return "text-red-500";
};

const scoreBg = (score) => {
  if (score >= 80) return "bg-emerald-500";
  if (score >= 60) return "bg-amber-500";
  return "bg-red-500";
};

const scoreLabel = (score) => {
  if (score >= 80) return "Healthy";
  if (score >= 60) return "Needs Attention";
  return "Critical";
};

const ScoreIcon = ({ score }) => {
  if (score >= 80) return <CheckCircle className="w-5 h-5 text-emerald-500" />;
  if (score >= 60) return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  return <XCircle className="w-5 h-5 text-red-500" />;
};

const HealthScores = ({ healthScores }) => {
  if (!healthScores) return null;

  const { modules, overall } = healthScores;

  return (
    <div className="space-y-4" data-testid="health-scores">
      {/* Overall Score */}
      <Card className="card-industrial">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-primary" />
              <div>
                <h3 className="font-semibold text-base">Overall Organization Health</h3>
                <p className="text-xs text-muted-foreground">Composite score across all modules</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${scoreColor(overall)}`}>{overall}</p>
              <Badge className={`text-xs ${scoreBg(overall)} text-white`}>
                {scoreLabel(overall)}
              </Badge>
            </div>
          </div>
          <Progress value={overall} className="h-3" />
        </CardContent>
      </Card>

      {/* Module Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((mod) => (
          <Card key={mod.module} className="card-industrial" data-testid={`health-${mod.module.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: mod.color }} />
                  <h4 className="font-medium text-sm">{mod.module}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <ScoreIcon score={mod.score} />
                  <span className={`text-xl font-bold ${scoreColor(mod.score)}`}>{mod.score}</span>
                </div>
              </div>

              <Progress value={mod.score} className="h-2 mb-3" />

              <div className="space-y-1">
                {mod.factors.map((f, i) => (
                  <p key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-muted-foreground flex-shrink-0" />
                    {f}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default HealthScores;
